'use client';

import { c as _c } from "react/compiler-runtime";
import { ReactElement, RefObject, useCallback, useEffect, useRef } from 'react';
import { ScarabCommand, ScarabItem } from '@spa/lib/emarsys/ScarabQueue';
import { resolvePageCountry, resolvePageLanguage, resolveUserLoginStatus } from '@spa/lib/tracking/resolvePageViewProperties';
import { routeChangeComplete, routeChangeStart } from '@spa/lib/store/router/routerSlice';
import { useSelector, useStore } from 'react-redux';
import Cookies from 'js-cookie';
import { CurrencyIso } from '@spa/lib/converters/convertCurrencyIso';
import { OptionalStateCarts } from '@spa/lib/store/carts/CartsState';
import { PageTemplate } from '@spa/lib/cms/const';
import type { PageType } from '@spa/lib/tracking/getPageType';
import { Session } from 'next-auth';
import type { UserLoginStatus } from '@spa/lib/tracking/pageViewTrackingEventSchema';
import { UsercentricsEvent } from '@spa/lib/usercentrics/interfaces';
import { consumeSuppressedSearchEvent } from '@spa/lib/tracking/searchEventSuppression';
import expireTime from '@spa/lib/configs/expireTime';
import getAuthSessionUserId from '@spa/lib/auth/getAuthSessionUserId';
import getDistributorCodes from '../../lib/usercentrics/getDistributorCodes';
import getPageType from '@spa/lib/tracking/getPageType';
import { guestUid } from '@spa/lib/const';
import { installTranslationResilience } from 'translation-resilience';
import isAnonymousCacheableCookieName from '@spa/lib/isAnonymousCacheableCookieName';
import logger from '@spa/lib/logger';
import { pushScarabCommands } from '@spa/lib/emarsys/pushScarabCommands';
import selectAccessToken from '@spa/lib/store/auth/selectors/selectAccessToken';
import selectAuthenticatedUserId from '@spa/lib/store/auth/selectors/selectAuthenticatedUserId';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import selectOptionalCurrencyIso from '@spa/lib/store/settings/selectors/selectOptionalCurrencyIso';
import selectOptionalSessionContent from '@spa/lib/store/session/selectors/selectOptionalSessionContent';
import { setAcceptedCookieConsents } from '@spa/lib/store/cookieConsents/cookieConsentsSlice';
import { setAuthState } from '@spa/lib/store/auth/authSlice';
import trackInit from '@spa/lib/tracking/trackInitActions';
import trackSearchActions from '@spa/lib/tracking/trackSearchActions';
import { tracker } from '@spa/lib/tracking/tracker';
import useDisplayLoadingBarIfAnyTaskPending from '@spa/lib/react/hooks/useDisplayLoadingBarIfAnyTaskPending';
import { usePathname } from 'next/navigation';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
import { useSession } from 'next-auth/react';
if (typeof window !== 'undefined') {
  ((): void => {
    installTranslationResilience();
    const shouldReloadBasedOnSessionStorage = (): boolean => {
      let shouldReload = false;
      if (sessionStorage) {
        const hardNavigationTimestampStr = sessionStorage.getItem('bstr-hard-navigation-ts');
        if (hardNavigationTimestampStr !== null) {
          const hardNavigationTimestamp = parseInt(hardNavigationTimestampStr);
          shouldReload = hardNavigationTimestamp + expireTime * 1000 * 2 >= Date.now();
        }
      }
      return shouldReload;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lWindow = window as any;
    lWindow.shouldForceHardNavigation = (): boolean => {
      let shouldForceHardNavigation = lWindow.BSTR_FORCE_HARD_NAVIGATION === true || shouldReloadBasedOnSessionStorage();
      if (!shouldForceHardNavigation && lWindow.BSTR_ANY_VISITED_PAGE_WAS_CACHED === true) {
        const allCookies = Object.keys(Cookies.get());
        shouldForceHardNavigation = allCookies.some(cookieName => !isAnonymousCacheableCookieName(cookieName));
      }
      return shouldForceHardNavigation;
    };
  })();
}
export interface Props {
  readonly cmsPageTemplate: PageTemplate;
  readonly remoteClientAddress: string | null;
}
interface PageViewSnapshot {
  readonly pageType: PageType;
  readonly userLoginStatus: UserLoginStatus;
  readonly pageLocation: string;
  readonly pageReferrer: string;
}
interface PageViewIdentity {
  readonly anonymousId: string;
  readonly authenticatedId: string | null;
}
const buildPageViewPayload = (snapshot: PageViewSnapshot, currencyIso: CurrencyIso, identity: PageViewIdentity): {
  page_type: PageType;
  page_language: string;
  page_country: string;
  page_currency: CurrencyIso;
  page_location: string;
  page_referrer: string;
  user_properties: {
    user_login_status: UserLoginStatus;
  };
  anonymousId: string;
  authenticatedId: string | null;
} => ({
  page_type: snapshot.pageType,
  page_language: resolvePageLanguage(),
  page_country: resolvePageCountry(),
  page_currency: currencyIso,
  page_location: snapshot.pageLocation,
  page_referrer: snapshot.pageReferrer,
  user_properties: {
    user_login_status: snapshot.userLoginStatus
  },
  anonymousId: identity.anonymousId,
  authenticatedId: identity.authenticatedId
});
const isCsr = typeof window !== 'undefined';
let persistedPreviousPath: string | null = null;
let lastTrackedSearchQuery: string | undefined = undefined;
let persistedPendingInitialPageView: PageViewSnapshot | null = null;
let persistedPendingVirtualPageViews: ReadonlyArray<PageViewSnapshot> = [];
const PREVIOUS_PAGE_REFERRER_STORAGE_KEY = 'bstr-previous-page-referrer';
const persistPreviousPageReferrer = (referrer: string): void => {
  try {
    sessionStorage.setItem(PREVIOUS_PAGE_REFERRER_STORAGE_KEY, referrer);
  } catch {
    // noop
  }
};
const readPersistedPreviousPageReferrer = (): string | null => {
  try {
    return sessionStorage.getItem(PREVIOUS_PAGE_REFERRER_STORAGE_KEY);
  } catch {
    return null;
  }
};
function CmsPageComponent(t0) {
  const $ = _c(68);
  const {
    cmsPageTemplate,
    remoteClientAddress
  } = t0;
  const store = useStore();
  const stateAccessToken = useSelector(selectAccessToken);
  const stateAuthenticatedUserId = useSelector(selectAuthenticatedUserId);
  const currencyIso = useSelector(selectOptionalCurrencyIso);
  const {
    data: session,
    status
  } = useSession();
  const authSession = session;
  const accessToken = authSession?.accessToken ?? null;
  let t1;
  if ($[0] !== authSession) {
    t1 = getAuthSessionUserId(authSession);
    $[0] = authSession;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const authenticatedUserId = t1;
  const anonymousUserId = useSelector(selectOptionalSessionContent)?.guid ?? null;
  const carts = useSelector(selectCarts);
  let cartEntries;
  if ($[2] !== carts) {
    cartEntries = [];
    for (const cart of Object.values(carts)) {
      if (cart) {
        cartEntries.push(...cart.entries.map(_temp));
      }
    }
    $[2] = carts;
    $[3] = cartEntries;
  } else {
    cartEntries = $[3];
  }
  const prevCartEntriesRef = useRef(cartEntries);
  const previousPath = useRef(persistedPreviousPath);
  const pendingInitialPageView = useRef(persistedPendingInitialPageView);
  const pendingVirtualPageViews = useRef(persistedPendingVirtualPageViews);
  const pathname = usePathname();
  const queryParams = useRuntimeSearchParams();
  let t2;
  if ($[4] !== queryParams) {
    t2 = queryParams.toString();
    $[4] = queryParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const queryString = t2;
  const fullPath = queryString ? `${pathname}?${queryString}` : pathname;
  let t3;
  if ($[6] !== queryParams) {
    t3 = queryParams.get("q");
    $[6] = queryParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const searchQueryParamStr = t3;
  let t4;
  if ($[8] !== queryParams) {
    t4 = queryParams.get("redirected");
    $[8] = queryParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const redirectedParam = t4;
  useDisplayLoadingBarIfAnyTaskPending();
  let t5;
  if ($[10] !== authenticatedUserId || $[11] !== carts || $[12] !== remoteClientAddress) {
    t5 = () => {
      trackInit(carts, authenticatedUserId, remoteClientAddress);
    };
    $[10] = authenticatedUserId;
    $[11] = carts;
    $[12] = remoteClientAddress;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const triggerTrackInit = t5;
  let t6;
  if ($[14] !== authenticatedUserId || $[15] !== cmsPageTemplate || $[16] !== pathname) {
    t6 = pageReferrer => ({
      pageType: getPageType({
        pathname,
        pageTemplate: cmsPageTemplate
      }),
      userLoginStatus: resolveUserLoginStatus(authenticatedUserId),
      pageLocation: window.location.href,
      pageReferrer
    });
    $[14] = authenticatedUserId;
    $[15] = cmsPageTemplate;
    $[16] = pathname;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const captureCurrentPageViewSnapshot = t6;
  let t7;
  let t8;
  if ($[18] !== accessToken || $[19] !== authenticatedUserId || $[20] !== stateAccessToken || $[21] !== stateAuthenticatedUserId || $[22] !== status || $[23] !== store) {
    t7 = () => {
      if (status !== "loading" && (stateAccessToken !== accessToken || stateAuthenticatedUserId !== authenticatedUserId)) {
        store.dispatch(setAuthState({
          accessToken,
          authenticatedUserId
        }));
      }
    };
    t8 = [accessToken, authenticatedUserId, stateAccessToken, stateAuthenticatedUserId, status, store];
    $[18] = accessToken;
    $[19] = authenticatedUserId;
    $[20] = stateAccessToken;
    $[21] = stateAuthenticatedUserId;
    $[22] = status;
    $[23] = store;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[26] !== cmsPageTemplate || $[27] !== pathname || $[28] !== queryParams) {
    t9 = () => {
      if (!pathname.includes("/no-results")) {
        lastTrackedSearchQuery = undefined;
        return;
      }
      const query = queryParams.get("q");
      if (!query || query === lastTrackedSearchQuery) {
        return;
      }
      lastTrackedSearchQuery = query;
      if (consumeSuppressedSearchEvent()) {
        return;
      }
      trackSearchActions(query, 0, {
        pathname,
        pageTemplate: cmsPageTemplate
      }, {
        query,
        totalCount: "0",
        items: []
      });
    };
    t10 = [pathname, queryParams, cmsPageTemplate];
    $[26] = cmsPageTemplate;
    $[27] = pathname;
    $[28] = queryParams;
    $[29] = t10;
    $[30] = t9;
  } else {
    t10 = $[29];
    t9 = $[30];
  }
  useEffect(t9, t10);
  let t11;
  if ($[31] !== authSession?.user || $[32] !== carts || $[33] !== fullPath || $[34] !== pathname || $[35] !== searchQueryParamStr) {
    t11 = () => {
      const prevPathValue = previousPath.current;
      const triggerRouteChange = prevPathValue !== fullPath;
      const cartEntries_0 = [];
      let guestEmail = null;
      for (const cart_0 of Object.values(carts)) {
        if (cart_0) {
          cartEntries_0.push(...cart_0.entries.map(_temp2));
          const {
            type,
            email
          } = cart_0.user;
          if (type === guestUid) {
            guestEmail = email;
          }
        }
      }
      const isCartUpdated = JSON.stringify(cartEntries_0) !== JSON.stringify(prevCartEntriesRef.current);
      if (triggerRouteChange) {
        const commands = [];
        const userEmail = authSession?.user.email ?? guestEmail;
        if (userEmail) {
          commands.push(["setEmail", userEmail]);
        }
        commands.push(["cart", cartEntries_0]);
        const isSearchRoute = fullPath.includes("/search");
        if (isSearchRoute && searchQueryParamStr) {
          commands.push(["searchTerm", searchQueryParamStr]);
        }
        if (!pathname.includes("/c/") && !pathname.includes("/p/") && !pathname.includes("/confirmation/")) {
          commands.push(["go"]);
        }
        pushScarabCommands(...commands);
      } else {
        if (isCartUpdated) {
          pushScarabCommands(["cart", cartEntries_0], ["go"]);
          prevCartEntriesRef.current = cartEntries_0;
        }
      }
    };
    $[31] = authSession?.user;
    $[32] = carts;
    $[33] = fullPath;
    $[34] = pathname;
    $[35] = searchQueryParamStr;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== authSession || $[38] !== carts || $[39] !== fullPath || $[40] !== pathname || $[41] !== searchQueryParamStr) {
    t12 = [authSession, carts, fullPath, pathname, searchQueryParamStr];
    $[37] = authSession;
    $[38] = carts;
    $[39] = fullPath;
    $[40] = pathname;
    $[41] = searchQueryParamStr;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[43] !== redirectedParam) {
    t13 = () => {
      if (typeof window !== "undefined" && redirectedParam === "true") {
        const event = new CustomEvent("search-collector", {
          detail: {
            isSearchRedirect: true
          },
          bubbles: true,
          composed: true
        });
        window.dispatchEvent(event);
      }
    };
    t14 = [redirectedParam];
    $[43] = redirectedParam;
    $[44] = t13;
    $[45] = t14;
  } else {
    t13 = $[44];
    t14 = $[45];
  }
  useEffect(t13, t14);
  let t15;
  if ($[46] !== anonymousUserId || $[47] !== authenticatedUserId || $[48] !== captureCurrentPageViewSnapshot || $[49] !== currencyIso || $[50] !== fullPath || $[51] !== store || $[52] !== triggerTrackInit) {
    t15 = () => {
      const prevPathValue_0 = previousPath.current;
      if (prevPathValue_0 === null) {
        triggerTrackInit();
        const persistedReferrer = readPersistedPreviousPageReferrer();
        const snapshot = captureCurrentPageViewSnapshot(persistedReferrer ?? (document.referrer || ""));
        if (currencyIso !== null && anonymousUserId !== null) {
          tracker.customPageView(buildPageViewPayload(snapshot, currencyIso, {
            anonymousId: anonymousUserId,
            authenticatedId: authenticatedUserId
          }));
        } else {
          pendingInitialPageView.current = snapshot;
          if (isCsr) {
            persistedPendingInitialPageView = snapshot;
          }
        }
      } else {
        const triggerRouteChange_0 = prevPathValue_0 !== fullPath;
        if (triggerRouteChange_0) {
          store.dispatch(routeChangeStart());
          const pageReferrer_0 = `${window.location.origin}${prevPathValue_0}`;
          const snapshot_0 = captureCurrentPageViewSnapshot(pageReferrer_0);
          Promise.resolve().then(() => {
            store.dispatch(routeChangeComplete());
            triggerTrackInit();
            if (currencyIso !== null && anonymousUserId !== null) {
              tracker.virtualPageView(buildPageViewPayload(snapshot_0, currencyIso, {
                anonymousId: anonymousUserId,
                authenticatedId: authenticatedUserId
              }));
              if (isCsr) {
                persistPreviousPageReferrer(snapshot_0.pageReferrer);
              }
            } else {
              pendingVirtualPageViews.current = [...pendingVirtualPageViews.current, snapshot_0];
              if (isCsr) {
                persistedPendingVirtualPageViews = pendingVirtualPageViews.current;
              }
            }
          }).catch(_temp3);
        } else {
          if (currencyIso !== null && anonymousUserId !== null) {
            const identity = {
              anonymousId: anonymousUserId,
              authenticatedId: authenticatedUserId
            };
            if (pendingInitialPageView.current !== null) {
              tracker.customPageView(buildPageViewPayload(pendingInitialPageView.current, currencyIso, identity));
              pendingInitialPageView.current = null;
              if (isCsr) {
                persistedPendingInitialPageView = null;
              }
            }
            if (pendingVirtualPageViews.current.length > 0) {
              pendingVirtualPageViews.current.forEach(pendingSnapshot => {
                tracker.virtualPageView(buildPageViewPayload(pendingSnapshot, currencyIso, identity));
                if (isCsr) {
                  persistPreviousPageReferrer(pendingSnapshot.pageReferrer);
                }
              });
              pendingVirtualPageViews.current = [];
              if (isCsr) {
                persistedPendingVirtualPageViews = [];
              }
            }
          }
        }
      }
      previousPath.current = fullPath;
      if (isCsr) {
        persistedPreviousPath = fullPath;
      }
    };
    $[46] = anonymousUserId;
    $[47] = authenticatedUserId;
    $[48] = captureCurrentPageViewSnapshot;
    $[49] = currencyIso;
    $[50] = fullPath;
    $[51] = store;
    $[52] = triggerTrackInit;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== anonymousUserId || $[55] !== authenticatedUserId || $[56] !== captureCurrentPageViewSnapshot || $[57] !== cmsPageTemplate || $[58] !== currencyIso || $[59] !== fullPath || $[60] !== pathname || $[61] !== store || $[62] !== triggerTrackInit) {
    t16 = [store, triggerTrackInit, captureCurrentPageViewSnapshot, currencyIso, anonymousUserId, authenticatedUserId, fullPath, pathname, cmsPageTemplate];
    $[54] = anonymousUserId;
    $[55] = authenticatedUserId;
    $[56] = captureCurrentPageViewSnapshot;
    $[57] = cmsPageTemplate;
    $[58] = currencyIso;
    $[59] = fullPath;
    $[60] = pathname;
    $[61] = store;
    $[62] = triggerTrackInit;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[64] !== store) {
    t17 = () => {
      const handleConsentEvent = event_0 => {
        const acceptedDistributors = getDistributorCodes(event_0);
        store.dispatch(setAcceptedCookieConsents({
          acceptedCookieConsents: acceptedDistributors
        }));
      };
      window.addEventListener("UC_CONSENT", handleConsentEvent as EventListener);
      return () => {
        window.removeEventListener("UC_CONSENT", handleConsentEvent as EventListener);
      };
    };
    t18 = [store];
    $[64] = store;
    $[65] = t17;
    $[66] = t18;
  } else {
    t17 = $[65];
    t18 = $[66];
  }
  useEffect(t17, t18);
  let t19;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <></>;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  return t19;
}
function _temp3(error) {
  logger.error({
    error
  }, "Failed to complete client route change");
}
function _temp2(t0) {
  const {
    product: t1,
    quantity: quantity_0,
    totalPrice: t2
  } = t0;
  const {
    code: code_0
  } = t1;
  const {
    valueWithTax: valueWithTax_0
  } = t2;
  return {
    item: code_0,
    price: valueWithTax_0,
    quantity: quantity_0
  };
}
function _temp(t0) {
  const {
    product: t1,
    quantity,
    totalPrice: t2
  } = t0;
  const {
    code
  } = t1;
  const {
    valueWithTax
  } = t2;
  return {
    item: code,
    price: valueWithTax,
    quantity
  };
}
export const resetPageViewTrackingStateForTesting = (): void => {
  persistedPreviousPath = null;
  persistedPendingInitialPageView = null;
  persistedPendingVirtualPageViews = [];
};
export default CmsPageComponent;